import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { CostFormSchema, CostsModel } from '../models';

const CostsModelElement = CostsModel.element;

export const useCosts = <T = Array<z.infer<typeof CostsModelElement>>>({
  select,
  params,
}: {
  params?: { showMigrated?: true; referenceFiscalYear?: string };
  select?: (data: z.infer<typeof CostsModel>) => T;
}) => {
  const { showMigrated, referenceFiscalYear, ...restParams } = params ?? {};
  return useQuery({
    queryKey: ['costs', referenceFiscalYear].filter(
      (item) => item !== undefined,
    ),
    queryFn: async () => {
      const data = await axiosGet('/api/main-costs/', {
        ...restParams,
        referenceFiscalYear,
        ...(!showMigrated && { isMigrated: false }),
      });
      return CostsModel.parse(data);
    },
    select,
  });
};

type FormValues = z.infer<typeof CostFormSchema>;

export const useCreateCost = () => {
  return useMutation({
    mutationFn: async (values: FormValues) => {
      const { period, ...payload } = CostFormSchema.parse(values);
      return await axiosPost('/api/main-costs/', {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useUpdateCost = () => {
  return useMutation({
    mutationFn: async (values: FormValues & { id: string }) => {
      const { id, period, ...payload } = CostFormSchema.extend({
        id: z.string().uuid(),
      }).parse(values);
      return await axiosPatch(`/api/main-costs/${id}/`, {
        ...payload,
        startYear: period?.[0]?.year() ?? null,
        endYear: period?.[1]?.year() ?? null,
      });
    },
  });
};

export const useDeleteCost = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/main-costs/${id}/`);
      await invalidate(['costs']);
      return res;
    },
  });
};
